/**
 * Spam Detection and Validation Utilities
 * Detect suspicious patterns in user submissions
 */

/**
 * Check if text contains suspicious spam patterns
 */
export function containsSpam(text: string): { isSpam: boolean; reason?: string } {
  if (!text || typeof text !== 'string') {
    return { isSpam: false };
  }

  const lowerText = text.toLowerCase();

  // Check for excessive repetition
  const repeatedChars = /(.)\1{10,}/;
  if (repeatedChars.test(text)) {
    return { isSpam: true, reason: 'Excessive character repetition detected' };
  }

  // Check for excessive URLs
  const urlPattern = /(https?:\/\/|www\.)/gi;
  const urlMatches = text.match(urlPattern);
  if (urlMatches && urlMatches.length > 3) {
    return { isSpam: true, reason: 'Too many URLs detected' };
  }

  // Common spam keywords
  const spamKeywords = [
    'viagra', 'cialis', 'casino', 'lottery', 'prize', 'winner',
    'click here', 'buy now', 'limited time', 'act now', 'congratulations',
    'you won', 'claim your', 'free money', 'earn money', 'work from home',
    'bitcoin', 'cryptocurrency', 'investment opportunity'
  ];

  let spamScore = 0;
  for (const keyword of spamKeywords) {
    if (lowerText.includes(keyword)) {
      spamScore++;
    }
  }

  if (spamScore >= 3) {
    return { isSpam: true, reason: 'Multiple spam keywords detected' };
  }

  // Check for excessive capitalization
  const capitals = text.match(/[A-Z]/g);
  const totalLetters = text.match(/[A-Za-z]/g);
  if (totalLetters && capitals && capitals.length / totalLetters.length > 0.7) {
    return { isSpam: true, reason: 'Excessive capitalization' };
  }

  // Check for excessive special characters
  const specialChars = text.match(/[!@#$%^&*()]/g);
  if (specialChars && specialChars.length > text.length * 0.3) {
    return { isSpam: true, reason: 'Too many special characters' };
  }

  return { isSpam: false };
}

/**
 * Validate email format
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate phone number (Indian format)
 */
export function isValidPhone(phone: string): boolean {
  // Remove spaces, dashes, and parentheses
  const cleanPhone = phone.replace(/[\s\-()]/g, '');
  
  // Check Indian mobile: 10 digits starting with 6-9
  const indianMobile = /^[6-9]\d{9}$/;
  
  // Check with +91 prefix
  const withCountryCode = /^(\+91|91)?[6-9]\d{9}$/;
  
  return indianMobile.test(cleanPhone) || withCountryCode.test(cleanPhone);
}

/**
 * Sanitize text input (remove potential XSS)
 */
export function sanitizeInput(text: string): string {
  if (!text || typeof text !== 'string') return '';
  
  return text
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '') // Remove iframe tags
    .replace(/on\w+="[^"]*"/gi, '') // Remove inline event handlers
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .trim();
}

/**
 * Check if input is too short to be meaningful
 */
export function isTooShort(text: string, minLength: number = 10): boolean {
  if (!text) return true;
  return text.trim().length < minLength;
}

/**
 * Check if input is suspiciously long
 */
export function isTooLong(text: string, maxLength: number = 5000): boolean {
  if (!text) return false;
  return text.length > maxLength;
}

/**
 * Validate form data for suspicious patterns
 */
export interface FormValidationResult {
  isValid: boolean;
  errors: string[];
}

export function validateFormSubmission(data: {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
  subject?: string;
}): FormValidationResult {
  const errors: string[] = [];

  // Check name
  if (data.name) {
    if (isTooShort(data.name, 2)) {
      errors.push('Name is too short');
    }
    if (isTooLong(data.name, 100)) {
      errors.push('Name is too long');
    }
    const spamCheck = containsSpam(data.name);
    if (spamCheck.isSpam) {
      errors.push(`Invalid name: ${spamCheck.reason}`);
    }
  }

  // Check email
  if (data.email && !isValidEmail(data.email)) {
    errors.push('Invalid email format');
  }

  // Check phone
  if (data.phone && !isValidPhone(data.phone)) {
    errors.push('Invalid phone number format');
  }

  // Check message
  if (data.message) {
    if (isTooShort(data.message, 10)) {
      errors.push('Message is too short');
    }
    if (isTooLong(data.message, 5000)) {
      errors.push('Message is too long');
    }
    const spamCheck = containsSpam(data.message);
    if (spamCheck.isSpam) {
      errors.push(`Spam detected: ${spamCheck.reason}`);
    }
  }

  // Check subject
  if (data.subject) {
    const spamCheck = containsSpam(data.subject);
    if (spamCheck.isSpam) {
      errors.push(`Invalid subject: ${spamCheck.reason}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Detect honeypot field (hidden field that bots fill in)
 */
export function isHoneypotFilled(honeypotValue: any): boolean {
  return honeypotValue !== '' && honeypotValue !== null && honeypotValue !== undefined;
}

/**
 * Check submission timing (too fast = likely bot)
 */
export function isSubmittedTooFast(formOpenTime: number, minSeconds: number = 3): boolean {
  const now = Date.now();
  const elapsedSeconds = (now - formOpenTime) / 1000;
  return elapsedSeconds < minSeconds;
}

/**
 * Generate a simple CAPTCHA-like challenge
 */
export function generateSimpleChallenge(): { question: string; answer: number } {
  const num1 = Math.floor(Math.random() * 10) + 1;
  const num2 = Math.floor(Math.random() * 10) + 1;
  return {
    question: `What is ${num1} + ${num2}?`,
    answer: num1 + num2
  };
}

/**
 * Check if user agent looks suspicious
 */
export function isSuspiciousUserAgent(userAgent: string): boolean {
  if (!userAgent) return true;
  
  const suspiciousPatterns = [
    /bot/i,
    /crawler/i,
    /spider/i,
    /scrapy/i,
    /curl/i,
    /wget/i,
    /python/i,
    /java(?!script)/i
  ];
  
  return suspiciousPatterns.some(pattern => pattern.test(userAgent));
}

/**
 * Validate order data for suspicious patterns
 */
export function validateOrderData(orderData: {
  items: any[];
  total: number;
  address: any;
}): FormValidationResult {
  const errors: string[] = [];

  // Check items
  if (!orderData.items || orderData.items.length === 0) {
    errors.push('No items in order');
  }

  if (orderData.items && orderData.items.length > 50) {
    errors.push('Too many items in single order');
  }

  // Check total
  if (orderData.total <= 0) {
    errors.push('Invalid order total');
  }

  if (orderData.total > 1000000) {
    errors.push('Order total exceeds reasonable limit');
  }

  // Check address
  if (orderData.address) {
    const addressText = [
      orderData.address.streetAddress,
      orderData.address.city,
      orderData.address.pincode
    ].join(' ');
    
    const spamCheck = containsSpam(addressText);
    if (spamCheck.isSpam) {
      errors.push(`Invalid address: ${spamCheck.reason}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

export default {
  containsSpam,
  isValidEmail,
  isValidPhone,
  sanitizeInput,
  isTooShort,
  isTooLong,
  validateFormSubmission,
  isHoneypotFilled,
  isSubmittedTooFast,
  generateSimpleChallenge,
  isSuspiciousUserAgent,
  validateOrderData
};
